import React, { useState, useEffect, useCallback } from "react";
import { Form, Button, Container, Spinner } from "react-bootstrap";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";

const CreateAssets = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Retrieves the ID from the URL if editing
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [formData, setFormData] = useState({
    asset_type: "",
    asset_name: "",
    asset_description: "",
    asset_date: "",
    asset_amount: "",
    asset_useful_life_years: "",
    asset_location: "",
    asset_status: "active",
  });

  // 1. Fetch existing data if "id" exists (Edit Mode)
  useEffect(() => {
    if (id) {
      const fetchAssetDetails = async () => {
        setFetching(true);
        try {
          const payload = {
            customer_id: Number(localStorage.getItem("customer_id")) || 1005,
            api_key: localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
            user_id: localStorage.getItem("user_id") || 14857,
            asset_id: id // Send ID to get specific asset
          };
          const response = await simplePostCall(ApiConfig.vieweeassets, JSON.stringify(payload));
       
           
            const asset = response.data;
            setFormData({
              asset_type: asset.asset_type,
              asset_name: asset.asset_name,
              asset_description: asset.asset_description,
              asset_date: asset.asset_date.split('T'), // Format date for input
              asset_amount: asset.asset_amount,
              asset_useful_life_years: asset.asset_useful_life_years || "",
              asset_location: asset.asset_location || "",
              asset_status: asset.asset_status || "active",
            });
     
        } catch (error) {
          toast.error("Failed to load asset details");
        } finally {
          setFetching(false);
        }
      };
      fetchAssetDetails();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 2. Combined Submit Handler (Create OR Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.asset_type || !formData.asset_name || !formData.asset_amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const commonPayload = {
        customer_id: Number(localStorage.getItem("customer_id")) || 1005,
        api_key: localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
        language: "en",
        user_id: localStorage.getItem("user_id") || 14857,
        asset_type: formData.asset_type,
        asset_name: formData.asset_name,
        asset_description: formData.asset_description,
        asset_date: formData.asset_date,
        asset_amount: Number(formData.asset_amount),
        asset_useful_life_years: formData.asset_useful_life_years
          ? Number(formData.asset_useful_life_years)
          : null,
        asset_location: formData.asset_location,
        asset_status: formData.asset_status,
      };

      let response;
      if (id) {
        // UPDATE MODE
        const updatePayload = { ...commonPayload, asset_id: id };
        response = await simplePostCall(ApiConfig.Updateassets, JSON.stringify(updatePayload));
      } else {
        // CREATE MODE
        response = await simplePostCall(ApiConfig.createassets, JSON.stringify(commonPayload));
      }

      if (response && response.result) {
        toast.success(id ? "Asset updated successfully" : "Asset created successfully");
        setTimeout(() => navigate("/accountant/finance/asset"), 1500);
      } else {
        toast.error(response.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="text-center mt-5"><ScaleLoader color="#F4BB44" /></div>;

  return (
    <div className="create-wrapper">
      <ToastContainer />

      <div className="special-header panel-white-all">
        <h4 className="page-title">{id ? "Edit Asset" : "Create Asset"}</h4>
      </div>

      <div className="yc-list-body">
        <div className="card">
          <div className="card-body">
            <Form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                <Form.Select name="asset_type" value={formData.asset_type} onChange={handleChange} required>
                  <option value="">Select Category</option>
                  <option value="Buildings">Buildings (classrooms, labs)</option>
                  <option value="Vehicles">Vehicles (school buses)</option>
                  <option value="IT Equipment">IT Equipment (computers, printers)</option>
                  <option value="Furniture & Fixtures">Furniture & Fixtures</option>
                  <option value="Lab Equipment">Lab Equipment</option>
                  <option value="Projectors">Projectors</option>
                  <option value="Land">Land</option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Asset Name <span className="text-danger">*</span></Form.Label>
                <Form.Control name="asset_name" type="text" value={formData.asset_name} onChange={handleChange} required />
              </Form.Group>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-12">
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control name="asset_description" as="textarea" rows={3} value={formData.asset_description} onChange={handleChange} />
              </Form.Group>
            </div>
          </div>

          <div className="row g-3 mt-2">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Purchase Date</Form.Label>
                <Form.Control name="asset_date" type="date" value={formData.asset_date} onChange={handleChange} />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Purchase Cost <span className="text-danger">*</span></Form.Label>
                <Form.Control name="asset_amount" type="number" step="0.01" value={formData.asset_amount} onChange={handleChange} required />
              </Form.Group>
            </div>
          </div>

          <div className="row g-3 mt-2">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Useful Life (Years)</Form.Label>
                <Form.Control
                  name="asset_useful_life_years"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="e.g. 5"
                  value={formData.asset_useful_life_years}
                  onChange={handleChange}
                />
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Location</Form.Label>
                <Form.Control
                  name="asset_location"
                  type="text"
                  placeholder="e.g. Room 12, Block A"
                  value={formData.asset_location}
                  onChange={handleChange}
                />
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="asset_status"
                  value={formData.asset_status}
                  onChange={handleChange}
                >
                  <option value="active">Active</option>
                  <option value="damaged">Damaged</option>
                  <option value="disposed">Disposed</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <div className="mt-4 d-flex justify-content-end gap-2">
            <Button className="btn btn-secondary" onClick={() => navigate(-1)} disabled={loading}>Cancel</Button>
            <Button type="submit" className="btn btn-primary px-4" disabled={loading}>
              {loading ? <Spinner size="sm" /> : id ? "Update Asset" : "Create Asset"}
            </Button>
          </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAssets;